/**
 * @module components/Dashboard/AccreditationBadge
 * @description Insignia de acreditación del sistema ACSGC — gamificación visual de cumplimiento.
 *
 * Muestra el nivel alcanzado por la organización según su porcentaje de cumplimiento ISO:
 * - **0–20%**: INITIAL CORE — escudo gris, candado abierto
 * - **21–50%**: ACTIVE DEFENDER — escudo azul, candado cerrado
 * - **51–80%**: TACTICAL SECURITY — escudo completo azul/rojo
 * - **81–99%**: ADVANCED STRATEGIST — bordes plateados, laureles
 * - **100%**: GLOBAL CORE MASTER — efecto dorado/holográfico
 *
 * La insignia se renderiza como SVG inline para permitir animaciones CSS.
 *
 * @see ProgressDashboard que monta esta insignia con el porcentaje global
 * @see AccreditationLevel tipo que define los umbrales de nivel
 */

import React from 'react';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

export interface AccreditationLevel {
  id: string;
  name: string;
  nameEs: string;
  minPercentage: number;
  maxPercentage: number;
  statusMessage: string;
}

export const ACCREDITATION_LEVELS: AccreditationLevel[] = [
  {
    id: 'initial-core',
    name: 'INITIAL CORE',
    nameEs: 'Núcleo Inicial',
    minPercentage: 0,
    maxPercentage: 20,
    statusMessage: 'Protocolos de seguridad en fase de despliegue.'
  },
  {
    id: 'active-defender',
    name: 'ACTIVE DEFENDER',
    nameEs: 'Defensor Activo',
    minPercentage: 21,
    maxPercentage: 50,
    statusMessage: 'Defensas perimetrales activadas. Monitoreo en curso.'
  },
  {
    id: 'tactical-security',
    name: 'TACTICAL SECURITY',
    nameEs: 'Seguridad Táctica',
    minPercentage: 51,
    maxPercentage: 80,
    statusMessage: 'Infraestructura robustecida. Gestión de riesgos operativa.'
  },
  {
    id: 'advanced-strategist',
    name: 'ADVANCED STRATEGIST',
    nameEs: 'Estratega Avanzado',
    minPercentage: 81,
    maxPercentage: 99,
    statusMessage: 'Cumplimiento normativo de alto nivel. Listo para certificación.'
  },
  {
    id: 'global-core-master',
    name: 'GLOBAL CORE MASTER',
    nameEs: 'Maestro del Núcleo Global',
    minPercentage: 100,
    maxPercentage: 100,
    statusMessage: 'Seguridad Máxima. Inmunidad normativa total.'
  }
];

// =====================================================
// HOOK PARA CALCULAR NIVEL
// =====================================================

export function useAccreditationLevel(completionRate: number): AccreditationLevel {
  const rate = Math.round(completionRate);

  if (rate >= 100) return ACCREDITATION_LEVELS[4];
  if (rate >= 81) return ACCREDITATION_LEVELS[3];
  if (rate >= 51) return ACCREDITATION_LEVELS[2];
  if (rate >= 21) return ACCREDITATION_LEVELS[1];
  return ACCREDITATION_LEVELS[0];
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

interface AccreditationBadgeProps {
  completionRate: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showMessage?: boolean;
}

export const AccreditationBadge: React.FC<AccreditationBadgeProps> = ({
  completionRate,
  size = 'md',
  showLabel = true,
  showMessage = true
}) => {
  const level = useAccreditationLevel(completionRate);

  // Tamaños del badge
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  // Colores según nivel
  const getShieldColors = () => {
    switch (level.id) {
      case 'initial-core':
        return {
          leftSide: '#6B7280', // gris
          rightSide: '#6B7280', // gris
          border: '#4B5563',
          lockColor: '#9CA3AF',
          lockOpen: true,
          glow: false,
          laurels: false,
          holographic: false
        };
      case 'active-defender':
        return {
          leftSide: '#3B82F6', // azul
          rightSide: '#6B7280', // gris (aún no completo)
          border: '#1D4ED8',
          lockColor: '#60A5FA',
          lockOpen: false,
          glow: false,
          laurels: false,
          holographic: false
        };
      case 'tactical-security':
        return {
          leftSide: '#3B82F6', // azul
          rightSide: '#EF4444', // rojo
          border: '#1E40AF',
          lockColor: '#F87171',
          lockOpen: false,
          glow: true,
          laurels: false,
          holographic: false
        };
      case 'advanced-strategist':
        return {
          leftSide: '#3B82F6', // azul
          rightSide: '#EF4444', // rojo
          border: '#C0C0C0', // plateado
          lockColor: '#E5E7EB',
          lockOpen: false,
          glow: true,
          laurels: true,
          holographic: false
        };
      case 'global-core-master':
        return {
          leftSide: '#3B82F6', // azul
          rightSide: '#EF4444', // rojo
          border: '#FFD700', // dorado
          lockColor: '#FDE047',
          lockOpen: false,
          glow: true,
          laurels: true,
          holographic: true
        };
      default:
        return {
          leftSide: '#6B7280',
          rightSide: '#6B7280',
          border: '#4B5563',
          lockColor: '#9CA3AF',
          lockOpen: true,
          glow: false,
          laurels: false,
          holographic: false
        };
    }
  };

  const colors = getShieldColors();

  return (
    <div className="flex flex-col items-center">
      {/* Badge SVG */}
      <div className={`relative ${sizeClasses[size]}`}>
        {/* Efecto de brillo/glow */}
        {colors.glow && (
          <div
            className={`absolute inset-0 rounded-full blur-md opacity-50 ${
              colors.holographic ? 'animate-pulse bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500' : 'bg-blue-400'
            }`}
          />
        )}

        {/* Efecto holográfico para nivel máximo */}
        {colors.holographic && (
          <div className="absolute inset-0 rounded-full animate-spin-slow opacity-30">
            <div className="w-full h-full bg-gradient-conic from-yellow-200 via-amber-400 to-yellow-200 rounded-full" />
          </div>
        )}

        <svg
          viewBox="0 0 100 120"
          className={`relative z-10 ${sizeClasses[size]} drop-shadow-lg ${
            colors.holographic ? 'animate-badge-glow' : ''
          }`}
        >
          <defs>
            {/* Gradiente para efecto metálico */}
            <linearGradient id="metallic-left" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.leftSide} stopOpacity="1" />
              <stop offset="50%" stopColor={colors.leftSide} stopOpacity="0.8" />
              <stop offset="100%" stopColor={colors.leftSide} stopOpacity="1" />
            </linearGradient>
            <linearGradient id="metallic-right" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.rightSide} stopOpacity="1" />
              <stop offset="50%" stopColor={colors.rightSide} stopOpacity="0.8" />
              <stop offset="100%" stopColor={colors.rightSide} stopOpacity="1" />
            </linearGradient>

            {/* Gradiente dorado para nivel máximo */}
            {colors.holographic && (
              <linearGradient id="gold-shimmer" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700">
                  <animate attributeName="stop-color" values="#FFD700;#FFF8DC;#FFD700" dur="2s" repeatCount="indefinite" />
                </stop>
                <stop offset="50%" stopColor="#FFF8DC">
                  <animate attributeName="stop-color" values="#FFF8DC;#FFD700;#FFF8DC" dur="2s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#FFD700">
                  <animate attributeName="stop-color" values="#FFD700;#FFF8DC;#FFD700" dur="2s" repeatCount="indefinite" />
                </stop>
              </linearGradient>
            )}

            {/* Filtro de brillo */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Laureles (para niveles avanzados) */}
          {colors.laurels && (
            <g className="laurels" opacity="0.9">
              {/* Laurel izquierdo */}
              <path
                d="M15 50 Q5 60 10 75 Q15 65 20 70 Q10 55 15 50"
                fill={colors.holographic ? '#FFD700' : '#C0C0C0'}
                stroke={colors.holographic ? '#B8860B' : '#A0A0A0'}
                strokeWidth="0.5"
              />
              <path
                d="M12 60 Q2 70 7 85 Q12 75 17 80 Q7 65 12 60"
                fill={colors.holographic ? '#FFD700' : '#C0C0C0'}
                stroke={colors.holographic ? '#B8860B' : '#A0A0A0'}
                strokeWidth="0.5"
              />
              {/* Laurel derecho */}
              <path
                d="M85 50 Q95 60 90 75 Q85 65 80 70 Q90 55 85 50"
                fill={colors.holographic ? '#FFD700' : '#C0C0C0'}
                stroke={colors.holographic ? '#B8860B' : '#A0A0A0'}
                strokeWidth="0.5"
              />
              <path
                d="M88 60 Q98 70 93 85 Q88 75 83 80 Q93 65 88 60"
                fill={colors.holographic ? '#FFD700' : '#C0C0C0'}
                stroke={colors.holographic ? '#B8860B' : '#A0A0A0'}
                strokeWidth="0.5"
              />
            </g>
          )}

          {/* Escudo - Forma base */}
          <path
            d="M50 10 L85 25 L85 60 Q85 90 50 110 Q15 90 15 60 L15 25 Z"
            fill="none"
            stroke={colors.holographic ? 'url(#gold-shimmer)' : colors.border}
            strokeWidth={colors.laurels ? '4' : '3'}
            filter={colors.glow ? 'url(#glow)' : undefined}
          />

          {/* Lado izquierdo del escudo */}
          <path
            d="M50 10 L15 25 L15 60 Q15 90 50 110 L50 10"
            fill="url(#metallic-left)"
          />

          {/* Lado derecho del escudo */}
          <path
            d="M50 10 L85 25 L85 60 Q85 90 50 110 L50 10"
            fill="url(#metallic-right)"
          />

          {/* Línea central divisoria */}
          <line
            x1="50" y1="10"
            x2="50" y2="110"
            stroke={colors.border}
            strokeWidth="1"
            opacity="0.5"
          />

          {/* Candado */}
          <g transform="translate(35, 45)">
            {/* Cuerpo del candado */}
            <rect
              x="5" y="15"
              width="20" height="18"
              rx="3"
              fill={colors.lockColor}
              stroke={colors.border}
              strokeWidth="1.5"
            />

            {/* Arco del candado */}
            {colors.lockOpen ? (
              /* Candado abierto */
              <path
                d="M10 15 L10 8 Q10 2 15 2 Q20 2 20 8 L20 10"
                fill="none"
                stroke={colors.lockColor}
                strokeWidth="3"
                strokeLinecap="round"
              />
            ) : (
              /* Candado cerrado */
              <path
                d="M10 15 L10 8 Q10 2 15 2 Q20 2 20 8 L20 15"
                fill="none"
                stroke={colors.lockColor}
                strokeWidth="3"
                strokeLinecap="round"
              />
            )}

            {/* Ojo de cerradura */}
            <circle
              cx="15" cy="24"
              r="3"
              fill={colors.border}
            />
            <rect
              x="14" y="24"
              width="2" height="5"
              fill={colors.border}
            />
          </g>

          {/* Texto ACSGC (solo en niveles altos) */}
          {(level.id === 'advanced-strategist' || level.id === 'global-core-master') && (
            <text
              x="50" y="100"
              textAnchor="middle"
              fontSize="8"
              fontWeight="bold"
              fill={colors.holographic ? '#FFD700' : '#E5E7EB'}
              className={colors.holographic ? 'animate-pulse' : ''}
            >
              ACSGC
            </text>
          )}

          {/* Destellos para nivel máximo */}
          {colors.holographic && (
            <g className="sparkles">
              <circle cx="25" cy="30" r="2" fill="#FFF" opacity="0.8">
                <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="75" cy="35" r="1.5" fill="#FFF" opacity="0.6">
                <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="50" cy="20" r="2" fill="#FFF" opacity="0.7">
                <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.8s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
        </svg>
      </div>

      {/* Etiqueta del nivel */}
      {showLabel && (
        <div className="mt-2 text-center">
          <div className={`font-bold text-xs tracking-wider ${
            colors.holographic
              ? 'text-yellow-300 animate-pulse'
              : level.id === 'advanced-strategist'
              ? 'text-gray-200'
              : 'text-blue-200'
          }`}>
            {level.name}
          </div>
          <div className="text-xs text-blue-300 opacity-80">
            {level.nameEs}
          </div>
        </div>
      )}

      {/* Mensaje de estado */}
      {showMessage && (
        <p className={`mt-1 text-xs text-center max-w-[200px] ${
          colors.holographic ? 'text-yellow-200' : 'text-blue-200'
        } opacity-90`}>
          {level.statusMessage}
        </p>
      )}
    </div>
  );
};

export default AccreditationBadge;
